import * as RadPopover from '@radix-ui/react-popover'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  ArrowDown2Icon,
  ArrowUp2Icon,
  Separator as Separator_,
} from '@root/shared/ui-kit'

import { platforms } from '../../model/platform'
import { PLATFORM_LABEL_MAP } from '../../model/platform'
import { PlatformCircleIcon } from '../platform-icon/platform-circle-icon'

import type { Platform } from '../../model'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  container?: React.RefObject<HTMLDivElement | null> | undefined
  value?: Platform.Platform
  onChange?: (platform: Platform.Platform | undefined) => void
} & ClassNameProps

const DD_HEIGHT = 40
const LOGO_SIZE = 25

export const PlatformDropdown: React.FC<Props> = ({
  className,
  container,
  value,
  onChange,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = (item: Platform.Platform | undefined) => {
    onChange?.(item)
    setOpen(false)
  }

  const renderLabel = () => {
    if (value) {
      return (
        <>
          <PlatformCircleIcon platform={value} size={LOGO_SIZE} />
          {PLATFORM_LABEL_MAP[value]}
        </>
      )
    }

    return t('platform.all_platforms')
  }

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger open={open} onClick={() => setOpen(!open)} className={className}>
        <Label>{renderLabel()}</Label>
        <ArrowDown2Icon size={13} />
      </Trigger>
      <RadPopover.Portal container={container?.current}>
        <Content side="bottom" align="end">
          <AllPlatforms onClick={() => handleSelect(undefined)}>
            {t('platform.all_platforms')}
            <ArrowUp2Icon size={13} />
          </AllPlatforms>
          <Separator />
          {platforms.map((item, index) => (
            <React.Fragment key={item}>
              <Item onClick={() => handleSelect(item)}>
                <PlatformCircleIcon platform={item} size={LOGO_SIZE} />
                {PLATFORM_LABEL_MAP[item]}
              </Item>
              {index !== platforms.length - 1 && <Separator />}
            </React.Fragment>
          ))}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)``

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>`
  height: ${DD_HEIGHT}px;

  background: var(--background-1);
  color: var(--text-9);

  display: grid;
  overflow: hidden;
  align-items: center;
  cursor: pointer;

  grid-template-columns: 1fr auto;
  padding-inline: 13px 12px;
  border: 0;

  font-weight: 600;
  font-size: 11px;
  text-align: left;
  border-radius: 6px;

  transition: all 0.2s ease-in-out;

  svg {
    opacity: 0.7;
  }

  &:hover {
    color: var(--text-12);

    svg {
      opacity: 1;
    }
  }
`

const Label = styled.span`
  gap: 3px;

  overflow: hidden;
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;

  text-align: left;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Content = styled(RadPopover.Content)`
  background: var(--background-2);
  box-shadow: 0px 12px 40px 0px var(--appearanceA-3);
  border-radius: 6px;

  margin-top: -${DD_HEIGHT}px;

  // over search results
  z-index: 1;
`

const Item = styled.div`
  height: ${DD_HEIGHT}px;

  color: var(--text-11);
  border: 0;
  background: none;
  display: grid;
  align-items: center;
  overflow: hidden;
  padding-inline: 13px 12px;

  grid-template-columns: auto auto;
  text-align: left;
  font-weight: 600;
  font-size: 11px;

  justify-content: start;
  justify-items: start;
  gap: 4px;

  transition: all 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    color: var(--text-12);
  }
`

const AllPlatforms = styled(Item)`
  justify-content: space-between;
`

export const Separator = styled(Separator_)`
  background: var(--backgroundA-2);
  margin: 0 8px;
`
